import type { FunctionType } from "./server-execution.js";
import type { HttpActionDef, HttpActionRequest, HttpActionResponse } from "@gencow/core";

type LoggerLike = Pick<Console, "log" | "error">;

type RouteContext = {
  get(key: string): unknown;
  req: {
    method: string;
    url: string;
    path: string;
    param(): Record<string, string>;
    query(): Record<string, string>;
    raw: {
      headers: Headers;
    };
    json<T = unknown>(): Promise<T>;
    formData(): Promise<FormData>;
    arrayBuffer(): Promise<ArrayBuffer>;
    text(): Promise<string>;
  };
  json(body: unknown, status?: number, headers?: Record<string, string>): Response;
};

type RouteRegistrar = {
  get(path: string, handler: (context: RouteContext) => Promise<Response> | Response): void;
  post(path: string, handler: (context: RouteContext) => Promise<Response> | Response): void;
  put(path: string, handler: (context: RouteContext) => Promise<Response> | Response): void;
  delete(path: string, handler: (context: RouteContext) => Promise<Response> | Response): void;
  patch(path: string, handler: (context: RouteContext) => Promise<Response> | Response): void;
};

type BuildCtxResult = {
  auth: {
    getUserIdentity(): unknown;
  };
};

type ExecuteWithTimeout = <T>(fn: () => Promise<T>, kind: FunctionType, name: string) => Promise<T>;

export function createHttpActionRequest(c: RouteContext): HttpActionRequest {
  return {
    method: c.req.method,
    url: c.req.url,
    path: c.req.path,
    params: c.req.param() as Record<string, string>,
    query: c.req.query() as Record<string, string>,
    headers: Object.fromEntries(c.req.raw.headers.entries()),
    json: <T = unknown>() => c.req.json<T>(),
    formData: () => c.req.formData(),
    arrayBuffer: () => c.req.arrayBuffer(),
    text: () => c.req.text(),
  };
}

export function toHttpActionResponse(c: RouteContext, result: HttpActionResponse | Response): Response {
  if (result instanceof Response) {
    return result;
  }

  const status = result.status ?? 200;
  const headers = result.headers ?? {};

  if (result.body instanceof Response) {
    return result.body;
  }

  if (
    result.body instanceof ArrayBuffer ||
    result.body instanceof Uint8Array ||
    (result.body && typeof result.body === "object" && "byteLength" in result.body)
  ) {
    return new Response(result.body as ArrayBuffer, { status, headers });
  }

  return c.json(result.body ?? null, status as any, headers);
}

export function registerHttpActions(params: {
  app: RouteRegistrar;
  actions: HttpActionDef[];
  buildCtx: (context: RouteContext, action: HttpActionDef) => BuildCtxResult;
  executeWithTimeout: ExecuteWithTimeout;
  logger?: LoggerLike;
}): void {
  const logger = params.logger ?? console;

  for (const action of params.actions) {
    const method = action.method.toLowerCase() as keyof RouteRegistrar;
    params.app[method](action.path, async (c) => {
      const ctx = params.buildCtx(c, action);

      if (!action.isPublic) {
        const user = ctx.auth.getUserIdentity();
        if (!user) {
          return c.json({ error: "Authentication required" }, 401);
        }
      }

      const req = createHttpActionRequest(c);

      try {
        const result = await params.executeWithTimeout(
          () => action.handler(ctx as never, req),
          "httpAction",
          `${action.method} ${action.path}`,
        );
        return toHttpActionResponse(c, result);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        logger.error(`[httpAction] ${action.method} ${action.path} error:`, message);
        return c.json({ error: message }, 500);
      }
    });
  }

  if (params.actions.length > 0) {
    logger.log(`[gencow] 🌐 httpActions: ${params.actions.map((a) => `${a.method} ${a.path}`).join(", ")}`);
  }
}
